import { useTheme } from "@mui/material";
import {
  useUpdateAuditStatusMutation,
  useUpdateReportTaskMutation,
} from "app/store/transmission/api/transmissionDetailApi";
import {
  changeExpansion,
  editReportTask,
  loadReportTaskList,
  toggleSubmitting,
} from "app/store/transmission/slice/transmissionSlice";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { checkUserAuditAccess } from "utils/report";
import { showErrorToast, showSuccessToast } from "utils/toast";

const useReportTasks = (data) => {
  const {
    taskList,
    tasksExpanded,
    taskSubmitting,
    taskFocused,
    isEditReportTask,
  } = useSelector((state) => state.transmission);

  const { isArchiveReport } = useSelector((state) => state.archiveReport);
  const { currentUser } = useSelector((state) => state.auth);
  const [hasAuditAccess, setHasAuditAccess] = useState(false);
  const [isAudited, setIsAudited] = useState(false);

  const dispatch = useDispatch();
  const theme = useTheme();

  const [updateReportTask] = useUpdateReportTaskMutation();
  const initialTask = {
    index: null,
    taskId: null,
    description: "",
    status: "To Do",
  };

  const [task, setTask] = useState(initialTask);
  const [updateAuditStatus] = useUpdateAuditStatusMutation();

  useEffect(() => {
    const auditAccess = checkUserAuditAccess(currentUser, data);

    setHasAuditAccess(auditAccess);
    setIsAudited(data?.is_audited || false);
  }, [currentUser, data]);

  const handleTask = async () => {
    try {
      dispatch(toggleSubmitting({ type: "reportTasks", value: true }));

      let result;
      if (isEditReportTask) {
        result = await updateReportTask({
          task,
          reportId: data?._id,
          patientId: data?.patient?._id,
          patientName: data?.patient?.name,
          patientSerialNumber: data?.patient?.serial_number,
          account: data?.account,
          isEditTask: true,
        }).unwrap();
      } else {
        result = await updateReportTask({
          task,
          reportId: data?._id,
          patientId: data?.patient?._id,
          patientName: data?.patient?.name,
          patientSerialNumber: data?.patient?.serial_number,
          account: data?.account,
          isEditTask: false,
        }).unwrap();
      }

      if (!result?.error) {
        dispatch(loadReportTaskList({ reportId: data?._id }));
        dispatch(editReportTask(false));
        setTask(initialTask);
        showSuccessToast(result?.message);
      }
    } catch (error) {
      showErrorToast(error?.data?.message);
    } finally {
      dispatch(toggleSubmitting({ type: "reportTasks", value: false }));
    }
  };

  const handleEdit = (selectedIndex, selectedTask) => {
    dispatch(editReportTask(true));
    const payload = {
      index: selectedIndex,
      taskId: selectedTask?._id,
      description: selectedTask?.description,
      status: selectedTask?.status,
    };

    setTask(payload);
    dispatch(changeExpansion({ type: "tasks", value: true }));
  };

  const handleInputChange = async (updatedTask) => {
    const payload = {
      ...task,
      description: updatedTask,
      ...(hasAuditAccess && { audited_report: true }),
    };
    setTask(payload);
  };

  const handleAudit = async (status) => {
    try {
      const result = await updateAuditStatus({
        reportId: data?._id,
        status,
      }).unwrap();

      if (result) {
        showSuccessToast(result?.message);
        setIsAudited(status);
      }
    } catch (error) {
      const errorMessage = error?.message || error?.data?.message;
      showErrorToast(errorMessage);
    }
  };

  return {
    isArchiveReport,
    taskList,
    tasksExpanded,
    taskSubmitting,
    taskFocused,
    theme,
    isEditReportTask,
    dispatch,
    handleEdit,
    handleInputChange,
    task,
    setTask,
    initialTask,
    handleTask,
    currentUser,
    hasAuditAccess,
    handleAudit,
    isAudited,
  };
};

export default useReportTasks;
